import React, { useEffect } from 'react';
import { Loader } from 'lucide-react';
import { Navigate, Outlet, useLocation, useOutletContext } from 'react-router-dom';
import { useTheme } from '../../../components/common/Theme';
import { useToast } from '../../../components/common/Toast';
import {
  GATEWAY_HEADER_EYEBROW,
  GOVERNED_MCP_GATEWAY_PATHS,
  gatewaySubtitleForTab,
  gatewayTabFromPath,
  gatewayTitleForTab,
} from './governedMcpConstants';
import { logGovernedMcp } from './governedMcpLogger';
import { GovernedMcpProvider, useGovernedMcp } from './governedMcpStore';
import AiUsageLimitsTab from './tabs/AiUsageLimitsTab';
import ConfigurationTab from './tabs/ConfigurationTab';
import DashboardTab from './tabs/DashboardTab';
import ToolsTab from './tabs/ToolsTab';

type GatewayOutletContext = {
  isLight: boolean;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
};

function GatewayBody() {
  const { theme } = useTheme();
  const isLight = theme === 'white';
  const { showToast } = useToast();
  const location = useLocation();
  const { state, setTab, ready, loading, loadError, persistError, reload } = useGovernedMcp();
  const toast = (type: 'success' | 'error' | 'info', message: string) => showToast({ type, message });
  const activeTab = gatewayTabFromPath(location.pathname);
  const title = gatewayTitleForTab(activeTab);
  const subtitle = gatewaySubtitleForTab(activeTab);
  const isIndexPath =
    location.pathname === GOVERNED_MCP_GATEWAY_PATHS.root ||
    location.pathname === `${GOVERNED_MCP_GATEWAY_PATHS.root}/`;

  useEffect(() => {
    if (persistError) showToast({ type: 'error', message: persistError });
  }, [persistError, showToast]);

  useEffect(() => {
    if (state.activeTab === activeTab) return;
    logGovernedMcp('app', 'gateway_route_sync', { path: location.pathname, tab: activeTab });
    setTab(activeTab);
  }, [activeTab, location.pathname, setTab, state.activeTab]);

  return (
    <div className={`w-full min-w-0 -m-3 min-h-[calc(100vh-1.5rem)] ${isLight ? 'bg-slate-50' : 'bg-neutral-950'}`}>
      <header className={`px-6 pt-6 ${isLight ? 'bg-slate-50' : 'bg-neutral-950'}`}>
        <p className={`m-0 text-[11px] font-semibold uppercase tracking-[0.08em] ${isLight ? 'text-indigo-600' : 'text-indigo-400'}`}>
          {GATEWAY_HEADER_EYEBROW}
        </p>
        <h1 className={`mt-1 text-xl font-semibold ${isLight ? 'text-neutral-900' : 'text-neutral-50'}`}>
          {title}
        </h1>
        <p className={`mt-1.5 max-w-xl text-[13px] ${isLight ? 'text-slate-500' : 'text-neutral-400'}`}>
          {subtitle}
        </p>
      </header>

      <div className="px-6 py-5">
        {!ready && loading && !isIndexPath ? (
          <div className={`flex items-center justify-center py-24 text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
            <Loader size={16} className="animate-spin mr-2" />
            Loading governed MCP state…
          </div>
        ) : !ready && loadError && !isIndexPath ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <p className={`text-sm font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>Could not load MCP Gateway</p>
            <p className={`text-sm mt-1 max-w-lg ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{loadError}</p>
            <button
              type="button"
              onClick={() => void reload()}
              className="mt-4 px-3 py-1.5 text-sm rounded-md bg-indigo-600 text-white"
            >
              Retry
            </button>
          </div>
        ) : (
          <Outlet context={{ isLight, onToast: toast } satisfies GatewayOutletContext} />
        )}
      </div>
    </div>
  );
}

function useGatewayPage(): GatewayOutletContext {
  return useOutletContext<GatewayOutletContext>();
}

export function GovernedMcpGatewayIndexRedirect() {
  return <Navigate to="dashboard" replace />;
}

export function GatewayDashboardPage() {
  const { isLight, onToast } = useGatewayPage();
  return <DashboardTab isLight={isLight} onToast={onToast} />;
}

export function GatewayToolsPage() {
  const { isLight, onToast } = useGatewayPage();
  return <ToolsTab isLight={isLight} onToast={onToast} />;
}

export function GatewayConfigurationPage() {
  const { isLight, onToast } = useGatewayPage();
  return <ConfigurationTab isLight={isLight} onToast={onToast} />;
}

export function GatewayAiUsagePage() {
  const { isLight, onToast } = useGatewayPage();
  return <AiUsageLimitsTab isLight={isLight} onToast={onToast} />;
}

export default function GovernedMcpGatewayScreen() {
  return (
    <GovernedMcpProvider>
      <GatewayBody />
    </GovernedMcpProvider>
  );
}
